import type { ReactNode } from "react";

/* Enkle line-ikoner (stroke = currentColor). Ingen emoji – konsistent,
   profesjonelt uttrykk på tvers av portalen. */

export type IconName =
  | "checkCircle"
  | "layers"
  | "flag"
  | "check"
  | "calendar"
  | "trendUp"
  | "buildings"
  | "target"
  | "activity"
  | "award"
  | "sun"
  | "moon"
  | "monitor"
  | "user"
  | "users"
  | "doc"
  | "panelLeft"
  | "chevronDown"
  | "home"
  | "grip"
  | "plus"
  | "bell"
  | "menu"
  | "logout"
  | "grid"
  | "chevronRight"
  | "chart"
  | "image"
  | "refresh"
  | "settings"
  | "chat"
  | "send"
  | "x";

const PATHS: Record<IconName, ReactNode> = {
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.5 2.5 4.5-5" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 13l9 5 9-5" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h12l-2.5 4L17 12H5" />
    </>
  ),
  check: (
    <>
      <path d="M4 12.5l4.5 4.5L20 6" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
    </>
  ),
  trendUp: (
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M17 7h4v4" />
    </>
  ),
  buildings: (
    <>
      <path d="M3 21h18" />
      <path d="M6 21V8l6-3v16" />
      <path d="M12 21V11l6 2.5V21" />
      <path d="M9 9v0M9 12v0M9 15v0" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  activity: (
    <>
      <path d="M3 12h4l2.5 7 5-14 2.5 7H21" />
    </>
  ),
  award: (
    <>
      <circle cx="12" cy="9" r="5.5" />
      <path d="M8.5 13.5L7 21l5-2.8L17 21l-1.5-7.5" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: (
    <>
      <path d="M20 14.5A8 8 0 019.5 4 7 7 0 1020 14.5z" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c0-4 3.5-6 7.5-6s7.5 2 7.5 6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.4 3-5.4 6.5-5.4s6.5 2 6.5 5.4" />
      <path d="M16 5.2a3.5 3.5 0 010 6.4M17 14.9c2 .7 3.5 2.4 3.5 5.1" />
    </>
  ),
  doc: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </>
  ),
  panelLeft: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </>
  ),
  chevronDown: (
    <>
      <path d="M6 9l6 6 6-6" />
    </>
  ),
  home: (
    <>
      <path d="M3.5 11.5L12 4l8.5 7.5" />
      <path d="M5.5 10v9.5h13V10" />
      <path d="M10 19.5v-6h4v6" />
    </>
  ),
  grip: (
    <>
      <circle cx="9" cy="6" r="1.2" />
      <circle cx="15" cy="6" r="1.2" />
      <circle cx="9" cy="12" r="1.2" />
      <circle cx="15" cy="12" r="1.2" />
      <circle cx="9" cy="18" r="1.2" />
      <circle cx="15" cy="18" r="1.2" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9.5a6 6 0 0112 0c0 3.6 1.3 5.1 2 6H4c.7-.9 2-2.4 2-6z" />
      <path d="M9.5 19a2.5 2.5 0 005 0" />
    </>
  ),
  // Fra ACL-portalenes ikonsett (samme strek og form)
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  logout: <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />,
  grid: <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />,
  chevronRight: <path d="M9 18l6-6-6-6" />,
  chart: <path d="M3 3v18h18M7 16v-4M11 16V8M15 16v-6M19 16V5" />,
  refresh: <path d="M21 12a9 9 0 0 1-15.5 6.2L3 15.5M3 12A9 9 0 0 1 18.5 5.8L21 8.5M21 3v5.5h-5.5M3 21v-5.5h5.5" />,
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M21 16l-5-5-9 9" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </>
  ),
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.9A8 8 0 1 1 21 12z" />,
  send: <path d="M21 3L10 14M21 3l-7 18-4-7-7-4 18-7z" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
};

export function Icon({
  name,
  size = 20,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
